import { FormEvent, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import intl from "react-intl-universal";
import { FiArrowRight, FiMail } from "react-icons/fi";
import api from "../../../Services/api";
import { FirstAccessShell } from "../MyFirstAccess/FirstAccessShell";
import {
  normalizeFirstAccessInput,
  prepareUsernameForApi,
  validateFirstAccessUsername,
} from "../MyFirstAccess/utils";

const t = (key: string, fallback: string): string => {
  const value = intl.get(key);
  return typeof value === "string" && value.trim() ? value : fallback;
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null) return undefined;

  const axiosLike = error as {
    message?: string;
    isAuthExpired?: boolean;
    response?: { data?: { description?: string; information?: string } };
  };

  const data = axiosLike.response?.data;
  if (typeof data?.information === "string" && data.information.trim()) {
    return data.information;
  }
  if (typeof data?.description === "string" && data.description.trim()) {
    return data.description;
  }
  if (axiosLike.isAuthExpired) {
    return t(
      "forgotPassword.requestError",
      "Não foi possível solicitar a redefinição de senha. Tente novamente."
    );
  }
  return undefined;
};

/**
 * Forgot password: CPF/e-mail → e-mail OTP (first_access) → resume MyFirstAccess
 * validate-code + set-password.
 */
const ForgotPassword: React.FC = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const validationError = validateFirstAccessUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = prepareUsernameForApi(username);
    setError("");
    setIsLoading(true);

    try {
      await api.post("/v1/auth/password-reset/request", {
        username: payload,
        ...(payload.includes("@") ? { email: payload } : { cpf: payload }),
      });

      history.push({
        pathname: "/auth/my-first-access/validate-code",
        state: {
          username: payload,
          otpAlreadySent: true,
          fromPasswordReset: true,
        },
      });
    } catch (err) {
      enqueueSnackbar(
        getApiErrorMessage(err) ||
          t(
            "forgotPassword.requestError",
            "Não foi possível solicitar a redefinição de senha. Tente novamente."
          ),
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FirstAccessShell
      title={t("forgotPassword.title", "Esqueceu a senha")}
      subtitle={t(
        "forgotPassword.subtitle",
        "Informe o CPF ou o e-mail cadastrado. Enviaremos um código de verificação para o e-mail da conta."
      )}
    >
      <form onSubmit={handleSubmit} className="login-form">
        <div className="login-field-group">
          <label className="login-field-label" htmlFor="forgot-password-username">
            {t("auth.documentOrEmail", "Documento ou e-mail")}
          </label>
          <div className="login-field">
            <span className="login-field__icon">
              <FiMail />
            </span>
            <input
              id="forgot-password-username"
              name="username"
              type="text"
              autoComplete="username"
              placeholder={t(
                "auth.documentOrEmailPlaceholder",
                "CPF, CNPJ ou e-mail"
              )}
              aria-label={t("auth.documentOrEmail", "Documento ou e-mail")}
              value={username}
              onChange={(event) => {
                setError("");
                setUsername(normalizeFirstAccessInput(event.currentTarget.value));
              }}
              className="login-field__input"
            />
          </div>
          {error ? <p className="login-field__error">{error}</p> : null}
        </div>

        <div className="login-verification-actions" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="login-verification-btn login-verification-btn--outline"
            onClick={() => history.push("/auth")}
            disabled={isLoading}
          >
            {t("words.back", "Voltar")}
          </button>
          <button
            type="submit"
            className="login-submit-btn"
            disabled={isLoading}
            style={{ flex: 1 }}
          >
            {isLoading ? (
              <>
                <span className="login-submit-btn__spinner" />
                {t("forgotPassword.sending", "Enviando…")}
              </>
            ) : (
              <>
                {t("forgotPassword.sendCode", "Avançar")}
                <FiArrowRight />
              </>
            )}
          </button>
        </div>
      </form>
    </FirstAccessShell>
  );
};

export default ForgotPassword;
